import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from '../lib/AxiosGo';
import { io } from 'socket.io-client';

const AppSettingsContext = createContext();

export const AppSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    app_name: 'Layanan Pengaduan Polda Jateng',
    app_description: 'Layanan Bot WhatsApp Pengaduan Masyarakat',
    logo: 'assets/images/logo.png',
    logo_light: 'assets/images/logo-light.png',
    logo_icon: 'assets/images/logo-icon.png',
    favicon: 'assets/images/favicon.png'
  });
  const [loading, setLoading] = useState(true);

  const socketUrl = process.env.REACT_APP_SOCKET_IO_URL || 'http://localhost:4000';

  const getMediaUrl = useCallback((urlPath) => {
    if (!urlPath) return '';
    if (urlPath.startsWith('http://') || urlPath.startsWith('https://')) {
      return urlPath;
    }
    if (urlPath.startsWith('uploads/')) {
      return `${socketUrl}/${urlPath}`;
    }
    if (urlPath.startsWith('/uploads/')) {
      return `${socketUrl}${urlPath}`;
    }
    // Relative static asset path
    if (!urlPath.startsWith('/')) {
      return `/${urlPath}`;
    }
    return urlPath;
  }, [socketUrl]);

  const updateFavicon = useCallback((faviconUrl) => {
    if (!faviconUrl) return;
    const url = getMediaUrl(faviconUrl);
    
    // Update all icon links
    const iconLinks = document.querySelectorAll("link[rel*='icon']");
    if (iconLinks.length > 0) {
      iconLinks.forEach((link) => {
        link.href = url;
      });
    } else {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = url;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [getMediaUrl]);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get('/settings');
      if (response.data && response.data.data) {
        setSettings(response.data.data);
        if (response.data.data.favicon) {
          updateFavicon(response.data.data.favicon);
        }
      }
    } catch (error) {
      console.error('Failed to fetch app settings:', error);
    } finally {
      setLoading(false);
    }
  }, [updateFavicon]);

  useEffect(() => {
    fetchSettings();

    const socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });

    socket.on('settings_updated', (updatedData) => {
      setSettings(updatedData);
      if (updatedData.favicon) {
        updateFavicon(updatedData.favicon);
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [fetchSettings, socketUrl, updateFavicon]);

  return (
    <AppSettingsContext.Provider
      value={{
        settings,
        setSettings,
        loading,
        refreshSettings: fetchSettings,
        getMediaUrl,
        updateFavicon
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => {
  const context = useContext(AppSettingsContext);
  if (!context) {
    throw new Error('useAppSettings must be used within an AppSettingsProvider');
  }
  return context;
};
